import React, { useState } from 'react';

import { toast } from 'react-toastify';
import { useMutation } from '@apollo/client';

import { useUserStrategies } from '../../../../context/UserStrategies';

import { Loading } from '../../../UI';
import { CustomNumber } from '../../../CustomNumber/CustomNumber';
import { ImageAndName } from './Utils';
import { Icon } from '../../../UI';

import {
  computePercentage,
  formatDate,
  getSymbol,
} from '../../../../helpers/functions';

import { DELETE_STRATEGY_ASSET_TARGET } from '../../../../http/apollo-queries';

import { IStrategyAsset } from '../../../../types/model';
import { Strategy } from '../../../NewStrategy/types';
import { ProfitLoss } from '../../../ProfitLoss';

interface AssetTargetDetailsProps {
  goBack: () => void;
  assetData: IStrategyAsset;
  currency: Strategy['currency'];
}

const AssetTargetDetails: React.FC<AssetTargetDetailsProps> = ({
  goBack,
  assetData,
  currency,
}) => {
  const {
    asset: { name, logo, type, symbol, history },
  } = assetData;
  const lastPrice = history[0]?.[currency.toLocaleLowerCase()];

  const { strategies, refetch: refetchStrategies } = useUserStrategies();
  const {
    strategyAssetTargets: { items: assetTargets },
  } = strategies
    .reduce(
      (arr, { strategyAssets: { items } }) => [...arr, ...items],
      [] as IStrategyAsset[]
    )
    .find(({ id }) => id === assetData.id)!;

  const [deleteStrategyAssetTarget] = useMutation(
    DELETE_STRATEGY_ASSET_TARGET,
    {
      onError: () => {
        toast.error(
          'There was an error processing your request, please try again'
        );
      },
    }
  );

  const [loading, setLoading] = useState(false);

  function removeAssetTarget(id: string) {
    setLoading(true);
    deleteStrategyAssetTarget({
      variables: {
        strategyAssetTargetId: id,
      },
    })
      .then(() => refetchStrategies())
      .finally(() => setLoading(false));
  }

  return loading ? (
    <div className="w-full flex justify-center">
      <Loading status={true} size="large" />
    </div>
  ) : (
    <div className="w-full h-full flex flex-col">
      <div className="flex items-center py-2 px-4 border-b border-grey-10">
        <Icon
          svgName="arrowBack"
          className="mr-4 cursor-pointer"
          onClick={goBack}
        />
        <ImageAndName name={name} logo={logo} symbol={symbol} type={type!} />
      </div>
      <div className="my-3  overflow-y-auto">
        {assetTargets.map((target, index, arr) => {
          return (
            <div
              key={index}
              className={`mb-4 border-grey-10 ${
                index !== arr.length - 1 && 'border-b'
              }`}
            >
              <div className="ml-3">
                <div className="flex justify-between mr-3">
                  <p className="text-base font-medium text-grey-50">S1</p>
                  <button
                    className="font-semibold text-sm"
                    onClick={() => removeAssetTarget(target.id!)}
                  >
                    Remove
                  </button>
                </div>
                <div className="flex flex-col mt-8">
                  <p className="font-medium text-xxs mb-1">End date</p>
                  <p className="text-sm">{formatDate(target.endDate)}</p>
                </div>

                <div className="flex flex-col mt-8">
                  <p className="font-medium text-xxs mb-1">Stop profit</p>
                  <CustomNumber value={target.stopProfit} />
                </div>

                <div className="flex flex-col mt-8">
                  <p className="font-medium text-xxs mb-1">
                    Stop Profit Variation
                  </p>
                  <ProfitLoss
                    isRow={false}
                    percentage={Math.abs(
                      computePercentage(lastPrice, target.stopProfit, 100)
                    )}
                    performancePosition="value"
                    prefixPercentage="("
                    suffixPercentage="%)"
                    symbol={getSymbol(currency)}
                    value={target.stopProfit - lastPrice}
                  />
                </div>

                <div className="flex flex-col mt-8">
                  <p className="font-medium text-xxs mb-1">Stop loss</p>
                  <CustomNumber value={target.stopLoss} />
                </div>

                <div className="flex flex-col mt-8 mb-3">
                  <p className="font-medium text-xxs mb-1">
                    Stop loss Variation
                  </p>
                  <ProfitLoss
                    isRow={false}
                    percentage={computePercentage(
                      lastPrice,
                      target.stopLoss,
                      100
                    )}
                    performancePosition="value"
                    prefixPercentage="("
                    suffixPercentage="%)"
                    symbol={getSymbol(currency)}
                    value={lastPrice - target.stopLoss}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AssetTargetDetails;
